import { Feature } from "../phonemes/features";
import { ipaToFeatures, IpaType } from "../phonemes/ipa";
import { segmentPhonemes } from "../phonemes/segmentPhonemes";
import { filterQuery, FilterQueryError } from "./filterQuery";
import { getBasicPhonemesFromSegments, getLanguageSegments } from "./languageQuestions";

export type SortProp = 'name' | 'vowels' | 'vowelClusters' | 'consonants' | 'consonantClusters'
  | 'clicks' | 'tones' | 'trills' | 'implosives' | 'ejectives';
export type FilterProp = 'all' | 'extinct' | 'vowelClusters' | 'consonantClusters'
  | 'clicks' | 'tones' | 'trills' | 'implosives' | 'ejectives' | 'custom';

export const allSortProps: SortProp[] = [
  'name', 'vowels', 'vowelClusters', 'consonants', 'consonantClusters',
  'clicks', 'tones', 'trills', 'implosives', 'ejectives'];
export const allFilterProps: FilterProp[] = [
  'all', 'vowelClusters', 'consonantClusters',
  'clicks', 'tones', 'trills', 'implosives', 'ejectives', 'extinct', 'custom'];

/**
 * take lists of segments by language name & compute stats for all of them
 */
export class TrackLanguageFeatures {
  private readonly languageToSegments: Map<string, string> = new Map<string, string>();
  private readonly extinct: Set<string> = new Set();
  private readonly stats: Record<string, LanguageStats> = {};
  private sorted: string[] = [];

  constructor(sets?: Record<string, string>[]) {
    if (sets) {
      sets.forEach(set => this.add(set));
      this.compute();
    }
  }

  /** add another batch of languages */
  add(languageToSegments: Record<string, string>, tag?: 'extinct'): void {
    const names = Object.getOwnPropertyNames(languageToSegments);
    names.forEach(name => this.languageToSegments.set(name, languageToSegments[name]));
    if (tag === 'extinct') {
      names.forEach(name => this.extinct.add(name));
    }
  }

  /** take the current set of languages & segments and compute stats,
   * @param sets put anything not in this list in `otherIpa`
   */
  compute(sets?: string[]): void {
    this.languageToSegments.forEach((segments, language) => {
      const stats = getLanguageStats(segments, sets);
      this.stats[language] = stats;
    });
  }

  /** get the stats for a given language */
  lookupStats(language: string): LanguageStats {
    const stats = this.stats[language];
    return stats !== undefined ? stats
      : {
        segments: '', segmentList: [], chartIpa: [], otherIpa: [],
        vowels: [], vowelClusters: [], consonants: [], consonantClusters: [],
        clicks: [], tones: [], trills: [], implosives: [], ejectives: []
      };
  }

  // return a list of languages sorted by name
  sortedByName(): string[] {
    if (this.sorted.length === 0) {
      const names = Object.getOwnPropertyNames(this.stats);
      this.sorted = names.sort((a, b) => { a = a.toLowerCase(); b = b.toLowerCase(); return a === b ? 0 : a > b ? 1 : 0 });
    }
    return this.sorted;
  }

  sortedByCount(prop: SortProp): string[] {
    if (prop === 'name') {
      return this.sortedByName();
    }
    const names = Object.getOwnPropertyNames(this.stats);
    const sorted = names.sort((a, b) => {
      const count1 = (this.stats[a] as unknown as Record<string, string[]>)[prop].length;
      const count2 = (this.stats[b] as unknown as Record<string, string[]>)[prop].length;
      return count1 === count2 ? 0 : count1 < count2 ? 1 : -1;
    });
    return sorted;
  }

  /** filter to just the languages with the given property,
   * optionally starting from a given list, e.g., one that's been sorted */
  filteredByProp(prop: FilterProp, list?: string[]): string[] {
    const names = list ? list : Object.getOwnPropertyNames(this.stats);
    if (prop === 'all') {
      return names;
    } else if (prop === 'extinct') {
      return names.filter(name => this.extinct.has(name));
    }
    const filtered = names.filter(name => {
      return (this.stats[name] as unknown as Record<string, string[]>)[prop].length > 0;
    });
    return filtered;
  }

  /** apply a custom filter containing phonemes & features to include/exclude */
  customFilter(filter: string, list?: string[]): string[] | FilterQueryError {
    const languageToSegments = this.getMap(list);
    return filterQuery(languageToSegments, filter);
  }
  private getMap(list?: string[]): Map<string, string> {
    if (!list) {
      return this.languageToSegments;
    }
    const languageToSegments: Map<string, string> = new Map();
    list.forEach(l => languageToSegments.set(l, this.languageToSegments.get(l) as string));
    return languageToSegments;
  }
}

export interface LanguageStats {
  readonly segments: string;
  readonly segmentList: string[];
  /** all single character IPA + special sets passed to 'compute' */
  readonly chartIpa: string[];
  /** remaining segments not in 'chartIpa' */
  readonly otherIpa: string[];

  /** segments that are vowels */
  readonly vowels: string[];
  /** segments that include multiple vowels */
  readonly vowelClusters: string[];
  /** segments that are consonants */
  readonly consonants: string[];
  /** segments that include multiple consonants, other than affricates */
  readonly consonantClusters: string[];
  /** segments that are clicks */
  readonly clicks: string[];
  /** segments that are tones */
  readonly tones: string[];
  /** segments that are trills */
  readonly trills: string[];
  /** segments that are implosives */
  readonly implosives: string[];
  /** segments that are ejectives */
  readonly ejectives: string[];
}

function getLanguageStats(segments: string, sets?: string[]): LanguageStats {
  const segmentList = getLanguageSegments(segments);
  const chartIpa = getBasicPhonemesFromSegments(segmentList, sets);
  const otherIpa = sets ? segmentList.filter(s => !sets.includes(s)) :
    segmentList.filter(s => !chartIpa.includes(s));

  const vowels: string[] = [];
  const vowelClusters: string[] = [];
  const consonants: string[] = [];
  const consonantClusters: string[] = [];
  const clicks: string[] = [];
  const tones: string[] = [];
  const trills: string[] = [];
  const implosives: string[] = [];
  const ejectives: string[] = [];
  segmentList.forEach(s => {
    const pieces = segmentPhonemes(s, 'ipa', 'all-aff');
    const phonemes = pieces.phonemes.map(p => p.phoneme)
    const features = getAllFeatures(phonemes);
    if (features.has('vowel')) {
      vowels.push(s);
      if (phonemes.length > 1) {
        vowelClusters.push(s);
      }
    } else if (features.has('tone')) {
      tones.push(s);
    } else {
      consonants.push(s);
      if (phonemes.length > 1) {
        consonantClusters.push(s);
      }
    }

    if (features.has('click')) {
      clicks.push(s);
    }
    if (features.has('trill')) {
      trills.push(s);
    }
    if (features.has('implosive')) {
      implosives.push(s);
    }
    if (features.has('ejective')) {
      ejectives.push(s);
    }
  });
  return {
    segments, segmentList, otherIpa, chartIpa,
    vowels, vowelClusters, consonants, consonantClusters,
    clicks, tones, trills, implosives, ejectives
  };
}

/** combine the features of each character */
function getAllFeatures(segment: IpaType[]): Set<Feature> {
  const set: Set<Feature> = new Set();
  for (let i = 0; i < segment.length; i++) {
    const s = segment[i];
    const features = ipaToFeatures(s);
    if (features) {
      features.forEach(f => set.add(f));
    }
  }
  return set;
}
